'use client';

import React, { useRef, useEffect, useState } from 'react'
import Headline from '@/components/ui/Headline/Headline';
import BrandSunMotif from '@/components/ui/BrandSunMotifs/BrandSunMotif';
import PrimaryButton from '@/components/ui/PrimaryButton/PrimaryButton';
import HorizontalTab from '@/components/ui/HorizontalTab/HorizontalTab';
import HorizontalTabItem from '@/components/ui/HorizontalTabItem/HorizontalTabItem';
import { LanguageType, LanguageDirection, BranchSectionContent } from '@/lib/types';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import Image from 'next/image';
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface LocationSectionProps {
  language?: LanguageType
  onInitialPosition?: (position: number) => void
  branchInfoSectionPageData: BranchSectionContent
  showHeadline?: boolean
  hideSecondImage?: boolean
  shouldAnimate?: boolean
  subheadingText?: string
  headingText?: string
  isLandingPage?: boolean
  onOpenSlidingForm?: () => void
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const LocationSection: React.FC<LocationSectionProps> = ({
  language = "english",
  onInitialPosition,
  branchInfoSectionPageData,
  showHeadline = true,
  hideSecondImage = false,
  shouldAnimate = true,
  subheadingText = "OUR LOCATION",
  headingText = "Visit our oasis in the heart of Riyadh",
  isLandingPage = false,
  onOpenSlidingForm,
  id,
}) => {
  const navigate = useAnimatedNavigate();
  const isLg = useIsLg();
  const sectionRef = useRef<HTMLElement>(null);
  const [activeBranchIndex, setActiveBranchIndex] = useState(0);
  const openingHoursRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current && onInitialPosition) {
      const rect = sectionRef.current.getBoundingClientRect();
      const bottomY = window.scrollY + rect.bottom;
      onInitialPosition(bottomY);
    }
  }, [isLg, onInitialPosition]);

  useGSAP(() => {
    const openingHours = openingHoursRef.current;
    const location = locationRef.current;

    if (!openingHours || !location) return;

    const elements = [openingHours, location];

    if (shouldAnimate) {
      gsap.set(elements, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: openingHours,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      tl.to(elements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });
    }

  }, { dependencies: [isLg], revertOnUpdate: true });

  useGSAP(() => {
    const section = sectionRef.current;
    if (section && isLg) {
      const images: HTMLElement[] = Array.from(section.querySelectorAll('.location-parallax-image'));

      images.forEach(image => {
        gsap.fromTo(image, {
          yPercent: -10
        }, {
          yPercent: 10,
          ease: "none",
          force3D: true,
          scrollTrigger: {
            trigger: image,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            onEnter: () => {
              image.style.willChange = "transform";
            },
            onLeave: () => {
              image.style.willChange = "auto";
            },
            onEnterBack: () => {
              image.style.willChange = "transform";
            },
            onLeaveBack: () => {
              image.style.willChange = "auto";
            }
          }
        });
      });
    }
  }, { dependencies: [activeBranchIndex, isLg], revertOnUpdate: true });

  const activeBranch = branchInfoSectionPageData.branches[activeBranchIndex];

  const finalTimetableHeading = activeBranch.timetableHeading ?? "Opening Hours";
  const finalLocationHeading = activeBranch.locationHeading ?? "How to find us";
  const finalButtonText = activeBranch.buttonText ?? "Join Rivive";
  const finalSatWedLabel = activeBranch.satWedLabel ?? "Sat – Wed";
  const finalThursdayLabel = activeBranch.thursdayLabel ?? "Thursday";
  const finalFridayLabel = activeBranch.fridayLabel ?? "Friday";
  const finalGoogleMapsButtonText = activeBranch.googleMapsButtonText ?? "Find us on Google Maps";

  return (
    <section
      ref={sectionRef}
      id={id}
      className={`
        w-full
        padding-main-x-mobile
        padding-main-x-desktop
        ${isLandingPage ? '' : 'pt-[1rem] lg:pt-0'}
        flex flex-col items-center justify-center
      gap-y-[2rem] lg:gap-y-[4.5rem]
      `}>
      {showHeadline && (
        <div className="
          flex items-center justify-center
          w-full container-x-large
          ">
          <Headline
            subheading={subheadingText}
            heading={headingText}
            type='short'
            alignment='center'
            language={language}
          />
        </div>
      )}

      <div className="
          flex flex-col
          gap-[2rem] lg:gap-[4.5rem]
          items-center justify-center
          w-full container-x-large
          ">

        {branchInfoSectionPageData.branches.length > 1 && (
          <div className="
            w-full max-w-[37rem]
            ">
            <HorizontalTab
              activeIndex={activeBranchIndex}
              onActiveIndexChange={setActiveBranchIndex}
              size={isLg ? 20 : 16}
            >
              {branchInfoSectionPageData.branches.map((branch, index) => (
                <HorizontalTabItem key={index} text={branch.branchName} language={language} size={isLg ? 20 : 16}>
                </HorizontalTabItem>
              ))}
            </HorizontalTab>
          </div>
        )}

        <div
          style={{
            height: isLg ? `min(calc(100svh - 2rem), 49.5rem)` : ``,
            minHeight: isLg ? '49.5rem' : ''
          }}
          className="
          w-full max-w-[42rem]
          lg:max-w-full
          flex flex-col lg:flex-row lg:justify-center gap-[0.75rem] midxl:gap-[1rem]
            ">
          <div className="
              order-2
              lg:order-1
              relative
              overflow-hidden
              border-radius-main
              outline-secondary
              outline-stroke-soft-200
              lg:min-w-[24.3125rem]
              lg:flex-grow
              lg:max-w-full
            ">

            <div className="relative aspect-3/4 tb:aspect-5/3 lg:w-full lg:h-full overflow-hidden">
              <Image
                src={activeBranch.image1.url}
                alt={activeBranch.image1AltText}
                fill
                sizes="(min-width: 992px) 33vw, 100vw"
                loading={isLandingPage ? "lazy" : "eager"}
                className="location-parallax-image"
                style={{ objectFit: 'cover', scale: 1.1 }}
              />
            </div>

          </div>

          <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className='
              order-1
              lg:order-2
              flex flex-col
              w-full lg:min-w-[30.6875rem] lg:flex-grow lg:max-w-[37rem]
              gap-y-[2.5rem] lg:gap-y-[3.5rem]
              bg-bg-light-primary
              outline-secondary
              outline-stroke-soft-200
              border-radius-main
              px-[2rem]
              lg:px-[3.5rem]
              pt-[2.5rem]
              lg:pt-[4.5rem]
              pb-[2rem]
              lg:pb-[2rem]
              items-center justify-center
              '>
            <div className='
               w-full max-w-[24.3125rem]
               flex flex-col
               items-center justify-center
               gap-[2rem] lg:gap-[6rem]
              '>
              <div ref={openingHoursRef} className='w-full flex flex-col gap-[1.5rem]'>
                <div className='w-full flex items-center justify-center'>
                  <BrandSunMotif type='sunshine' />
                </div>
                <h3 className={`
                  text-36-to-24--neue-june
                  text-text-primary text-center
                `}>
                  {finalTimetableHeading}
                </h3>
                <div className={`flex flex-col gap-[0.75rem] w-full items-center justify-center
                    text-text-quaternary
                    text-20--alexandria
                  `}>
                  <div className='flex flex-row flex-wrap gap-[0.75rem] items-center justify-between w-full'>
                    <p>
                      {finalSatWedLabel}:
                    </p>
                    <p>
                      {activeBranch.openingHoursSatToWed}
                    </p>
                  </div>
                  <div className='flex flex-row flex-wrap gap-[0.75rem] items-center justify-between w-full'>
                    <p>
                      {finalThursdayLabel}:
                    </p>
                    <p>
                      {activeBranch.openingHoursThu}
                    </p>
                  </div>
                  <div className='flex flex-row flex-wrap gap-[0.75rem] items-center justify-between w-full'>
                    <p>
                      {finalFridayLabel}:
                    </p>
                    <p>
                      {activeBranch.openingHoursFri}
                    </p>
                  </div>
                </div>
              </div>

              <div ref={locationRef} className='w-full flex flex-col items-center justify-center gap-[1.5rem]'>
                <h3 className={`
                     text-36-to-24--neue-june
                     text-text-primary text-center
                  `}>
                  {finalLocationHeading}
                </h3>

                <p style={{ whiteSpace: 'pre-line' }} className={`
                    text-20--alexandria
                    text-text-quaternary text-center`}>
                  {activeBranch.branchAddress}
                </p>

                {!isLandingPage && <div style={{ direction: "ltr" }} className='w-fit'>
                  <PrimaryButton
                    text={finalGoogleMapsButtonText}
                    size={isLg ? 56 : 52}
                    type='ghost-reversed'
                    wrapText={true}
                    language={language}
                    onClick={() => window.open(activeBranch.branchAddressLink, '_blank')}
                  />
                </div>}
              </div>
            </div>
            <div style={{ direction: "ltr" }} className='w-full'>
              <PrimaryButton
                text={finalButtonText}
                icon={false}
                size={isLg ? 64 : 52}
                type='accent'
                language={language}
                onClick={() => {
                  if (isLandingPage && onOpenSlidingForm) {
                    onOpenSlidingForm();
                  } else {
                    navigate('/signup');
                  }
                }}
                isLPCtaButton={isLandingPage}
              />
            </div>
          </div>

          {!hideSecondImage && (
            <div className="
              order-3
              lg:order-3
              relative
              overflow-hidden
              border-radius-main
              outline-secondary
              outline-stroke-soft-200
              lg:min-w-[24.3125rem]
              lg:flex-grow
              lg:max-w-full
              block
              lg:hidden
              midxl2:block
            ">

              <div className="relative aspect-3/4 tb:aspect-5/3 lg:w-full lg:h-full overflow-hidden">
                <Image
                  src={activeBranch.image2.url}
                  alt={activeBranch.image2AltText}
                  fill
                  sizes="(min-width: 992px) 33vw, 100vw"
                  loading={isLandingPage ? "lazy" : "eager"}
                  className="location-parallax-image"
                  style={{ objectFit: 'cover', scale: 1.1 }}
                />
              </div>

            </div>
          )}
        </div>
      </div>
    </section>
  )
};

export default LocationSection;
